import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import api, { errMsg } from "../api";

export const EQUIP_STATUSES = ["Pending Contact", "Contacted", "Order Processing", "Ordered", "Ready/Shipped", "Completed", "Cancelled"];
const FIELDS = ["status", "admin_notes", "carrier", "tracking_number", "shipping_address", "city", "state", "zip"];

export default function EquipmentManageDialog({ request, onClose, onSaved }) {
  const { toast } = useToast();
  const [f, setF] = useState({});
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (request) setF(Object.fromEntries(FIELDS.map((k) => [k, request[k] || ""]))); }, [request]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/equipment/${request.id}/status`, { ...f, notify_applicant: notify });
      const n = r.data.notified;
      toast({ title: "Request updated", description: n ? (n.email_sent ? "Applicant notified in-app and by email." : "Applicant notified in-app (email not sent).") : "Saved." });
      onSaved(); onClose();
    } catch (e) {
      toast({ title: "Update failed", description: errMsg(e), variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="equip-manage-dialog">
        <DialogHeader><DialogTitle>Manage equipment request</DialogTitle></DialogHeader>
        {request && (
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              <div className="font-semibold text-slate-800">{request.applicant_name} · {request.email} · {request.phone}</div>
              {request.notes && <div className="mt-1">Applicant notes: {request.notes}</div>}
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="equip-manage-status"><SelectValue /></SelectTrigger>
                <SelectContent>{EQUIP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Carrier</Label><Input value={f.carrier} onChange={set("carrier")} data-testid="equip-manage-carrier" /></div>
              <div className="space-y-1.5"><Label>Tracking number</Label><Input value={f.tracking_number} onChange={set("tracking_number")} data-testid="equip-manage-tracking" /></div>
            </div>
            <div className="space-y-1.5"><Label>Shipping address</Label><Input value={f.shipping_address} onChange={set("shipping_address")} data-testid="equip-manage-address" /></div>
            <div className="grid grid-cols-3 gap-3">
              <Input value={f.city} onChange={set("city")} placeholder="City" data-testid="equip-manage-city" />
              <Input value={f.state} onChange={set("state")} placeholder="State" data-testid="equip-manage-state" />
              <Input value={f.zip} onChange={set("zip")} placeholder="ZIP" data-testid="equip-manage-zip" />
            </div>
            <div className="space-y-1.5"><Label>Internal admin notes</Label><Textarea rows={3} value={f.admin_notes} onChange={set("admin_notes")} data-testid="equip-manage-notes" /></div>
            <label className="flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="equip-manage-notify" /> Notify applicant when the status changes</label>
            {(request.status_history || []).length > 0 && (
              <div className="rounded-lg border border-slate-100 p-2 text-xs text-slate-500">
                <div className="mb-1 font-semibold text-slate-700">Progress history</div>
                {request.status_history.slice().reverse().map((h, i) => <div key={i}>{new Date(h.at).toLocaleString()} — {h.from} → {h.to} ({h.by})</div>)}
              </div>
            )}
            <Button onClick={save} disabled={busy} className="btn-primary-gradient w-full border-0 text-white" data-testid="equip-manage-save">{busy ? "Saving…" : "Save changes"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
